<script>
import {computed, onMounted, ref} from 'vue';
import {useMenuStore} from '@/js/store/MenuStore.js';
import {useCartStore} from '@/js/store/CartStore.js';
import {useLikeStore} from '@/js/store/LikeStore.js';
import {useSwipePanel} from '@/js/useSwipePanel.js';
import IconLogo from '@js/components/Icons/IconLogo.vue';
import IconMapPoint from '@js/components/Icons/IconMapPoint.vue';
import IconAngleLeft from '@js/components/Icons/IconAngleLeft.vue';
import IconX from '@js/components/Icons/IconX.vue';
import SalesDepartmentPhone from '@js/components/Sections/Links/SalesDepartmentPhone.vue';
import {Link} from '@inertiajs/vue3';
import {useCompareStore} from "@js/store/CompareStore.js";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import {usePopupsControlStore} from "@js/store/PopupsControlStore.js";
import IconUser from "@js/components/Icons/IconUser.vue";
import MobileMenuAccordionBlock from "@js/components/Mobile/MobileMenuAccordionBlock.vue";

export default {
    name: "MobileProfileMenu",
    components: {
        MobileMenuAccordionBlock,
        IconUser,
        OnlyMobile,
        IconLogo,
        IconMapPoint,
        IconAngleLeft,
        IconX,
        SalesDepartmentPhone,
        Link,
    },
    setup() {
        const menuStore = useMenuStore();
        const cartStore = useCartStore();
        const likeStore = useLikeStore();
        const compareStore = useCompareStore();
        const popupStore = usePopupsControlStore();

        const panelRef = ref(null);
        const handleRef = ref(null);

        const isOpen = computed(() => menuStore.isProfileSidenavOpen);

        const {
            rendered,
            panelStyle,
            backdropStyle,
            close,
            handleBackgroundClick,
        } = useSwipePanel({
            isOpen,
            panelRef,
            handleRef,
            direction: 'left',
            closeThreshold: 90,
            openDuration: 300,
            closeDuration: 280,
            fadeDivisor: 220,
            lockScroll: true,
            onClose: () => {
                menuStore.closeProfileSidenav();
            },
        });

        const openCatalogMenu = () => {
            close();
            setTimeout(() => {
                menuStore.openSidenav();
            }, 280);
        };

        const openCitySelectPopup = () => {
            close();
            setTimeout(() => {
                popupStore.showCitySelectPopup();
            }, 280);
        };

        const closeMenu = () => {
            close();
        };

        const onLinkClick = () => {
            close();
        };

        const isClient = ref(false);

        onMounted(() => {
            isClient.value = true;
        });

        const mainLinks = [
            {
                title: 'Доставка',
                route_name: 'deliveryAndShipping',
            },
            {
                title: 'Способы оплаты',
                route_name: 'aboutPayment',
            },
            {
                title: 'Прайс-лист',
                route_name: 'priceList',
            },
        ];

        const userLinks = [
            {
                title: 'Избранные товары',
                route_name: 'likedProducts',
                badgeKey: 'liked',
            },
            {
                title: 'Сравнение товаров',
                route_name: 'compareProducts',
                badgeKey: 'compare',
            },
            {
                title: 'Смета',
                route_name: 'cart',
                badgeKey: 'cart',
            },
        ];

        const infoLinks = [
            {
                title: 'Контакты',
                route_name: 'contacts',
            },
            {
                title: 'О компании',
                route_name: 'aboutCompany',
            },
            {
                title: 'Политика конфиденциальности',
                route_name: 'privacyPolicy',
            },
        ];

        const getBadgeValue = (badgeKey) => {
            if (!isClient.value) return 0;
            if (badgeKey === 'liked') return likeStore.likedIds.length;
            if (badgeKey === 'cart') return cartStore.items.length;
            if (badgeKey === 'compare') return compareStore.comparedIds.length;
            return 0;
        };

        return {
            isClient,
            menuStore,
            cartStore,
            likeStore,
            compareStore,
            popupStore,
            panelRef,
            handleRef,
            rendered,
            panelStyle,
            backdropStyle,
            closeMenu,
            handleBackgroundClick,
            openCatalogMenu,
            openCitySelectPopup,
            mainLinks,
            userLinks,
            infoLinks,
            getBadgeValue,
            onLinkClick,
        };
    },
};
</script>

<template>
    <div v-if="rendered">
        <div
            class="fixed inset-0 z-40 bg-black/50"
            :style="backdropStyle"
            @click="handleBackgroundClick"
        ></div>

        <div
            ref="panelRef"
            class="fixed inset-y-0 left-0 z-50 w-full max-w-[480px] bg-white shadow-xl"
            :style="panelStyle"
        >
            <div
                ref="handleRef"
                class="absolute top-0 right-0 z-20 flex h-full w-3 items-center justify-center cursor-grab"
            >
                <div class="h-14 w-1 rounded-full bg-gray-300"></div>
            </div>

            <div class="flex h-full flex-col min-h-0">
                <div class="shrink-0 flex items-center justify-between px-4 pt-4 pb-4 border-b border-gray-200">
                    <IconLogo class="h-auto w-[190px]"/>

                    <button
                        @click.prevent="closeMenu"
                        title="Закрыть"
                        aria-label="Закрыть"
                        class="rounded-md focus:outline-none"
                    >
                        <IconX class="w-6 h-6"/>
                    </button>
                </div>

                <div class="flex-1 overflow-y-auto px-4 py-5">
                    <div class="pb-5 border-b border-gray-200">
                        <button
                            @click.prevent="openCatalogMenu"
                            class="w-full flex items-center justify-between font-medium text-left"
                        >
                            <span>Меню каталога</span>
                            <IconAngleLeft class="w-5 h-5 rotate-180"/>
                        </button>
                    </div>

                    <div class="flex flex-col gap-5 mt-5 pb-5 border-b border-gray-200">
                        <div
                            v-if="popupStore.selectedCity.name"
                            @click.prevent="openCitySelectPopup"
                            class="flex items-center justify-between font-medium cursor-pointer"
                        >
                            <div>
                                Город
                            </div>

                            <div class="flex items-center gap-2 text-sm">
                                <IconMapPoint class="w-5 h-5 text-navHoverColor"/>
                                <span>{{ popupStore.selectedCity.name }}</span>
                                <IconAngleLeft class="w-5 h-5 rotate-180"/>
                            </div>
                        </div>

                        <Link
                            v-for="(item, index) in mainLinks"
                            :key="`main-${index}`"
                            :href="route(item.route_name)"
                            class="block font-medium"
                            @click="onLinkClick"
                        >
                            <span>{{ item.title }}</span>
                        </Link>
                    </div>

                    <div class="flex flex-col gap-5 mt-5 pb-5 border-b border-gray-200">
                        <Link
                            v-for="(item, index) in userLinks"
                            :key="`user-${index}`"
                            :href="route(item.route_name)"
                            class="flex items-center justify-between font-medium"
                            @click="onLinkClick"
                        >
                            <span>{{ item.title }}</span>

                            <div
                                v-if="getBadgeValue(item.badgeKey)"
                                class="flex items-center justify-center min-w-[16px] h-[16px] px-1 bg-red-600 rounded-full text-xs font-semibold text-white"
                            >
                                {{ getBadgeValue(item.badgeKey) }}
                            </div>
                        </Link>
                    </div>

                    <div class="flex flex-col gap-5 mt-5 pb-5">
                        <div class="font-medium text-gray-500">
                            Информация
                        </div>

                        <Link
                            v-for="(item, index) in infoLinks"
                            :key="`info-${index}`"
                            :href="route(item.route_name)"
                            class="block font-medium"
                            @click="onLinkClick"
                        >
                            <span>{{ item.title }}</span>
                        </Link>
                    </div>

                    <div class="mt-6 pt-5 border-t border-gray-200">
                        <div class="text-sm text-gray-500 mb-2">
                            Отдел продаж
                        </div>
                        <SalesDepartmentPhone class="text-base font-semibold"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
</style>
